import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { toast } from 'sonner';
import { invalidateActivityLogDelayed } from '@/hooks/queries/useActivityLogQuery';

export const projectUserKeys = {
  all: ['projectUsers'] as const,
  lists: () => [...projectUserKeys.all, 'list'] as const,
  list: (projectId: number) => [...projectUserKeys.lists(), projectId] as const,
};

export const roleKeys = {
  all: ['roles'] as const,
};

/**
 * Fetch all users associated with a project
 */
export const useProjectUsersQuery = (projectId: number) => {
  return useQuery({
    queryKey: projectUserKeys.list(projectId),
    queryFn: () => apiClient.getProjectUsers(projectId),
    enabled: !!projectId,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch all available roles (cached indefinitely within session)
 */
export const useRolesQuery = () => {
  return useQuery({
    queryKey: roleKeys.all,
    queryFn: () => apiClient.getRoles(),
    gcTime: 30 * 60 * 1000,
    staleTime: 30 * 60 * 1000,
  });
};

/**
 * Assign a user to a project by email
 */
export const useAssignUserToProjectMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, email }: { projectId: number; email: string }) =>
      apiClient.assignUserToProject(projectId, email),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: projectUserKeys.list(projectId) });
      invalidateActivityLogDelayed(queryClient, projectId);
      toast.success('User added to project');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to add user');
    },
  });
};

/**
 * Change a user's role on a project
 */
export const useUpdateProjectUserRoleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, userId, roleId }: { projectId: number; userId: number; roleId: number }) =>
      apiClient.updateProjectUserRole(projectId, userId, roleId),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: projectUserKeys.list(projectId) });
      invalidateActivityLogDelayed(queryClient, projectId);
      toast.success('Role updated');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update role');
    },
  });
};

/**
 * Remove a user from a project
 */
export const useRemoveProjectUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, userId }: { projectId: number; userId: number }) =>
      apiClient.removeProjectUser(projectId, userId),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: projectUserKeys.list(projectId) });
      invalidateActivityLogDelayed(queryClient, projectId);
      toast.success('User removed from project');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to remove user');
    },
  });
};
